import React, { useState } from 'react';
import { Globe, ArrowRight, CheckCircle2, ShieldCheck, Clock, DollarSign, Phone, Sparkles, Building2 } from 'lucide-react';
import { companyInfo } from '../data/companyInfo';

interface MarketPageProps {
  initialMarket?: 'usa' | 'uk' | 'canada';
  onNavigate: (path: string) => void;
  onStartProject: () => void;
  onOpenConsultation: () => void;
}

export const MarketPage: React.FC<MarketPageProps> = ({
  initialMarket = 'usa',
  onNavigate,
  onStartProject,
  onOpenConsultation,
}) => {
  const [activeMarket, setActiveMarket] = useState<'usa' | 'uk' | 'canada'>(initialMarket);

  const marketData = {
    usa: {
      country: 'United States',
      flag: '🇺🇸',
      code: 'US',
      currency: 'USD ($)',
      headline: 'Enterprise Website Development, Custom Software & SEO for US Businesses',
      tagline: 'Bridging technical rigor and commercial growth across US Eastern, Central, and Pacific time zones.',
      spellingNotes: 'American English (Customized, Optimization, Labor-saving automation)',
      coreNeeds: [
        'High-Conversion B2B Website Development & Redesign',
        'Custom Cloud Software & Scalable SaaS Product Engineering',
        'Generative AI Integration & Customer Service Workflows',
        'Cross-Platform Hybrid Mobile Apps (React Native / Flutter)',
        'Technical SEO & Performance Search Marketing (Google Ads & PMax)',
        'Ongoing Monthly Website Maintenance & Security SLAs'
      ],
      timeZoneNotice: 'Daily sprint check-ins scheduled seamlessly within US business hours (9:00 AM – 6:00 PM EST / CST / PST).',
      commercialTerms: 'Contracting under transparent milestone billing in USD ($) with mutual Non-Disclosure Agreements (NDA) and 100% intellectual property (IP) assignment upon completion.',
      faqs: [
        {
          q: 'How does EBON Media collaborate with US-based clients across time zones?',
          a: 'We operate dedicated international engineering pods with 4 to 6 hours of daily real-time overlap across US Eastern (EST) and Pacific (PST) hours, utilizing Slack, Google Meet, and Jira.'
        },
        {
          q: 'Do you take care of our website after the launch?',
          a: 'Yes, absolutely. We provide ongoing monthly care retainers that include speed optimization, Core Web Vitals maintenance, security patches, technical SEO monitoring, and feature enhancements.'
        },
        {
          q: 'Can we pay in US Dollars (USD)?',
          a: 'Yes. All project estimates, milestone invoicing, and contracts for US partners are handled natively in USD ($) via wire transfer, ACH, or international payment portals.'
        }
      ]
    },
    uk: {
      country: 'United Kingdom',
      flag: '🇬🇧',
      code: 'UK',
      currency: 'GBP (£)',
      headline: 'Bespoke Web Design, Custom Software & Digital Marketing for UK Companies',
      tagline: 'Delivering technical craftsmanship, GDPR-ready architectures, and measurable commercial results across Great Britain.',
      spellingNotes: 'British English (Bespoke, Optimisation, Modernisation, Scalable programmes)',
      coreNeeds: [
        'Bespoke Corporate Web Design & Headless Development',
        'Custom Business Process Automation & Cloud Software',
        'Technical SEO & Organic Search Visibility for UK Markets',
        'Cross-Platform iOS & Android Mobile Application Services',
        'Headless E-Commerce Development & Shopping Integrations',
        'Continuous Website Care, Speed Profiling & Security SLAs'
      ],
      timeZoneNotice: 'Convenient GMT / BST schedule alignment with direct afternoon and morning video syncs.',
      commercialTerms: 'Clear project schedules quoted in GBP (£) with structured milestone deliverables, GDPR-compliant data standards, and complete source code ownership.',
      faqs: [
        {
          q: 'Are your digital solutions compliant with UK GDPR regulations?',
          a: 'Yes. Every website and software application we develop adheres strictly to UK GDPR data privacy guidelines, encrypted user storage, and transparent cookie governance.'
        },
        {
          q: 'How do you support ongoing growth after the initial build?',
          a: 'We establish ongoing partnership retainers focused on proactive website health, continuous keyword rankings, and digital marketing optimizations to generate steady client inquiries.'
        },
        {
          q: 'Can contracts and invoices be settled in British Pounds (GBP)?',
          a: 'Yes. Invoices and agreed service agreements are issued directly in GBP (£) with zero foreign exchange ambiguity.'
        }
      ]
    },
    canada: {
      country: 'Canada',
      flag: '🇨🇦',
      code: 'CA',
      currency: 'CAD ($)',
      headline: 'Full-Stack Web Development, AI Automation & Digital Marketing for Canadian Firms',
      tagline: 'Supporting Canadian SMBs, tech startups, and retail enterprises from Toronto to Vancouver with scalable digital platforms.',
      spellingNotes: 'Canadian English (Organized, Modernization, Multi-regional reach)',
      coreNeeds: [
        'Bilingual-Ready Website Design & Headless CMS Engineering',
        'Custom Software & ERP Process Modernization',
        'Hybrid Mobile App Development for iOS & Android',
        'Local & National Search Engine Optimization (SEO)',
        'Cross-Border E-Commerce Storefronts with Multi-Currency Checkout',
        'Long-Term Website Care, Performance Monitoring & Backups'
      ],
      timeZoneNotice: 'Full working-hour overlap with Canadian business centres across EST and PST zones.',
      commercialTerms: 'Transparent fixed-scope proposals quoted in CAD ($) or USD ($) with strict confidentiality and dedicated technical account leads.',
      faqs: [
        {
          q: 'Do you build bilingual (English & French) Canadian websites?',
          a: 'Yes. We engineer multi-lingual headless architectures with automated locale switching, localized URL routing, and bilingual SEO metadata.'
        },
        {
          q: 'How does EBON Media ensure our website keeps winning business?',
          a: 'We combine ongoing technical upkeep (Core Web Vitals, security, hosting health) with proactive SEO and paid acquisition strategies so your site continuously generates market pipeline.'
        },
        {
          q: 'Can we pay in Canadian Dollars (CAD)?',
          a: 'Yes. We support invoicing and contract settlements in CAD ($) via international bank transfers and enterprise payment systems.'
        }
      ]
    }
  };

  const market = marketData[activeMarket];

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16 min-h-screen">
      
      {/* Market Selector Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <Globe size={14} className="text-[#159DE8]" />
            <span>INTERNATIONAL CLIENT HUBS • USA • UK • CANADA</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Tailored Digital Partnerships for Your Market
          </h1>

          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            Select your region below to view market-specific service capabilities, time-zone collaboration arrangements, currency details, and long-term website care frameworks.
          </p>

          {/* Region Tabs */}
          <div className="inline-flex items-center p-1.5 rounded-2xl bg-[#050A13] border border-[#1b2f4a] gap-2 mt-2">
            {[
              { id: 'usa', label: '🇺🇸 United States', code: 'USA' },
              { id: 'uk', label: '🇬🇧 United Kingdom', code: 'UK' },
              { id: 'canada', label: '🇨🇦 Canada', code: 'Canada' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveMarket(tab.id as any)}
                className={`px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  activeMarket === tab.id
                    ? 'bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white shadow-md'
                    : 'text-[#8FA0B8] hover:text-white hover:bg-white/5'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Market Deep-Dive Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-card-dark space-y-10">
          
          {/* Market Headline */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-[#1b2f4a]">
            <div className="space-y-2 max-w-3xl">
              <div className="flex items-center gap-2 text-xs font-mono font-bold text-[#159DE8] uppercase tracking-wider">
                <span className="text-2xl">{market.flag}</span>
                <span>Services for {market.country} Businesses</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                {market.headline}
              </h2>
              <p className="text-sm text-[#D5DFEC] leading-relaxed">
                {market.tagline}
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-[#050A13] border border-[#1b2f4a] shrink-0 space-y-1 text-xs">
              <div className="text-[#8FA0B8]">Currency Invoicing:</div>
              <div className="font-mono font-bold text-lg text-[#FF851B]">{market.currency}</div>
              <div className="text-[11px] text-[#00C853] font-medium">Standard Milestone Contracts</div>
            </div>
          </div>

          {/* Core Service Deliverables for this Market */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-white font-display">
              High-Demand Services for {market.country} Clients:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {market.coreNeeds.map((need, i) => (
                <div key={i} className="p-4 rounded-xl bg-[#071426] border border-[#1b2f4a] flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#00C853] shrink-0 mt-0.5" />
                  <span className="text-xs font-semibold text-white leading-relaxed">{need}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Time Zone & Commercial Arrangements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            <div className="p-6 rounded-2xl bg-[#050A13] border border-[#1b2f4a] space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#159DE8] flex items-center gap-2">
                <Clock size={15} />
                <span>Working Hours & Communication</span>
              </div>
              <p className="text-xs sm:text-sm text-[#D5DFEC] leading-relaxed">
                {market.timeZoneNotice}
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#050A13] border border-[#1b2f4a] space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#FF851B] flex items-center gap-2">
                <ShieldCheck size={15} />
                <span>Commercial Terms & IP Ownership</span>
              </div>
              <p className="text-xs sm:text-sm text-[#D5DFEC] leading-relaxed">
                {market.commercialTerms}
              </p>
            </div>
          </div>

          {/* Market FAQs */}
          <div className="space-y-4 pt-6 border-t border-[#1b2f4a]">
            <h3 className="text-lg font-bold text-white font-display">
              Frequently Asked Questions for {market.country} Clients
            </h3>

            <div className="space-y-3">
              {market.faqs.map((f, i) => (
                <div key={i} className="p-5 rounded-xl bg-[#071426] border border-[#1b2f4a] space-y-1.5">
                  <div className="font-bold text-white text-xs sm:text-sm">{f.q}</div>
                  <div className="text-xs text-[#8FA0B8] leading-relaxed">{f.a}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Market Action Bar */}
          <div className="pt-6 border-t border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-[#8FA0B8]">
              Ready to collaborate on a high-performing digital platform for your {market.country} business?
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={onStartProject}
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all flex items-center justify-center gap-2"
              >
                <span>Start Your {market.code} Project</span>
                <ArrowRight size={14} />
              </button>

              <button
                onClick={onOpenConsultation}
                className="w-full sm:w-auto px-5 py-3 rounded-full bg-[#050A13] border border-[#1b2f4a] hover:border-white text-white text-xs font-semibold transition-all"
              >
                <span>Book a Strategy Call</span>
              </button>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};
